import { useEffect, useRef, useState } from "react";
import { navItemsForRole } from "@/components/app-nav";
import type { SmRole } from "@/domain/sm-models";
import type { BusinessProfile } from "@/lib/market-catalog";
import { fetchProfileNavConfig } from "@/lib/profile-nav-config";
import { fetchTenantModulesNav, type TenantModulesNav } from "@/lib/tenant-modules-nav";
import { useTenantContextSafe } from "@/lib/tenant-context";

export type MerchantNavSource = {
  role: SmRole | undefined;
  planId?: string | null;
  businessProfile?: BusinessProfile;
  operationalRoleId?: string | null;
};

/**
 * Modelo de navegación merchant (permisos, plan, perfil).
 * Desktop → Sidebar. Mobile → Navigation Hub. No comparte markup.
 */
export function useMerchantNavItems({
  role,
  planId,
  businessProfile,
  operationalRoleId,
}: MerchantNavSource) {
  const tenantCtx = useTenantContextSafe();
  const [navPathFlags, setNavPathFlags] = useState<Map<string, boolean> | undefined>(undefined);
  const [modulesNav, setModulesNav] = useState<TenantModulesNav | undefined>(undefined);
  const fetchedRef = useRef(false);
  const modulesFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (fetchedRef.current || !businessProfile) return;
    fetchedRef.current = true;
    fetchProfileNavConfig()
      .then((map) => setNavPathFlags(map.get(businessProfile)))
      .catch(() => {
        /* fallback al hardcode estático onlyProfiles */
      });
  }, [businessProfile]);

  // SCRUM-289: solo tiene efecto real para tenants con 2+ módulos activos —
  // ver navItemsForRole. Fetch liviano, cacheado (tenant-modules-nav.ts).
  useEffect(() => {
    const tenantId = tenantCtx?.tenantId;
    if (!tenantId || modulesFetchedRef.current === tenantId) return;
    modulesFetchedRef.current = tenantId;
    fetchTenantModulesNav(tenantId)
      .then(setModulesNav)
      .catch(() => {
        /* fallback: sin moduleIds, navItemsForRole usa el camino legacy */
      });
  }, [tenantCtx?.tenantId]);

  return navItemsForRole(role, planId, {
    businessProfile,
    operationalRoleId: operationalRoleId ?? undefined,
    navPathFlags,
    productFlags: tenantCtx?.productFlags,
    moduleIds: modulesNav?.moduleIds,
    moduleNavPaths: modulesNav?.moduleNavPaths,
  });
}
